import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message envoyé avec succès !");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Erreur lors de l'envoi du message");
    }
    setSending(false);
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Header */}
      <div className="bg-[#F5F5F5] py-16 lg:py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#699ab6] font-medium mb-3">
            Contact
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-medium text-[#333333] tracking-tight mb-4">
            Parlons de votre projet
          </h1>
          <p className="text-[#777777] text-base font-light max-w-xl">
            Que vous souhaitiez vendre, acheter ou simplement obtenir des conseils,
            notre équipe est à votre écoute.
          </p>
        </div>
      </div>

      {/* Content */}
      <section className="py-16 lg:py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl font-medium text-[#333333] mb-8">
                Nos coordonnées
              </h2>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-[#699ab6]" />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">
                      Téléphone
                    </p>
                    <a href="tel:+41223000262" className="text-sm text-[#333] hover:text-[#699ab6] transition-colors">
                      +41 22 300 02 62
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4 text-[#699ab6]" />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">
                      Email
                    </p>
                    <a href="mailto:info@vingt-neuf.ch" className="text-sm text-[#333] hover:text-[#699ab6] transition-colors">
                      info@vingt-neuf.ch
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-[#699ab6]" />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">
                      Adresse
                    </p>
                    <p className="text-sm text-[#333]">
                      Rue Ancienne 55<br />
                      1227 Carouge, Genève
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-[#699ab6]" />
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-1">
                      Horaires
                    </p>
                    <p className="text-sm text-[#333]">
                      Lun - Ven : 9h00 - 18h00
                    </p>
                    <p className="text-sm text-[#777]">
                      Sam : Sur rendez-vous
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <h2 className="font-heading text-2xl font-medium text-[#333333] mb-8">
                Envoyez-nous un message
              </h2>

              <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-2">
                      Nom complet *
                    </label>
                    <input
                      data-testid="contact-form-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="input-underline w-full text-sm"
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-2">
                      Email *
                    </label>
                    <input
                      data-testid="contact-form-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="input-underline w-full text-sm"
                      placeholder="jean@exemple.ch"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-2">
                      Téléphone *
                    </label>
                    <input
                      data-testid="contact-form-phone"
                      type="tel"
                      required
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="input-underline w-full text-sm"
                      placeholder="+41 79 000 00 00"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-2">
                      Sujet
                    </label>
                    <input
                      data-testid="contact-form-subject"
                      type="text"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="input-underline w-full text-sm"
                      placeholder="Demande d'information"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#777] font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    data-testid="contact-form-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="input-underline w-full text-sm resize-none"
                    placeholder="Décrivez votre projet..."
                  />
                </div>

                <button
                  data-testid="contact-form-submit"
                  type="submit"
                  disabled={sending}
                  className="bg-[#699ab6] text-white px-10 py-4 text-[11px] uppercase tracking-[0.15em] font-medium hover:bg-[#5a8ba5] transition-colors disabled:opacity-50"
                >
                  {sending ? "Envoi en cours..." : "Envoyer le message"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
